import { applyTheming, DEFAULT_LOCALE, languageToi18nCode } from "@pandas/core-ui";
import { ApiError, type Location, NetworkError, type Partner } from "@pandas/http-client";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { servicesOrchestratorValues } from "@/config/services";
import { configuratorApi, platformApi } from "../../services";
import { useStore } from "../../store";
import logger from "../../utils/logging/logger";
import { clearStorageData } from "../../utils/session/storage";
import Button from "../core/Buttons/Button";
import { PopUp } from "../core/Modals/PopUp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../core/Select";

type PopupProps = {
	showPopup: boolean;
	setShowPopup?: (_value: boolean) => void;
};

export default function LocationPickerModal({ showPopup, setShowPopup }: PopupProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const clearHasPickedLocation = useStore((state) => state.clearHasPickedLocation);
	const clearPartnerData = useStore((state) => state.clearPartnerData);
	const setConfig = useStore((state) => state.setConfig);
	const setLocation = useStore((state) => state.setLocation);
	const setAttribute = useStore((state) => state.setAttribute);
	const setHasPickedLocation = useStore((state) => state.setHasPickedLocation);
	const [loading, setLoading] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<string | undefined>();
	const [platformPartners, setPlatformPartners] = useState<Partner[]>([]);
	const [partnerLocations, setPartnerLocations] = useState<Location[]>([]);
	const [selectedPartner, setSelectedPartner] = useState<string | undefined>();
	const [error, setError] = useState<string | null>(null);

	const handleLocationSave = async (selectedLocation: string) => {
		setError(null);
		setLoading(true);
		try {
			const locationData = await platformApi.getLocationAndPartner(selectedLocation);
			if (locationData) {
				const partnerConfig = await configuratorApi.getConfig(locationData.Partner.code);
				const theming = applyTheming(partnerConfig, locationData.Partner.code, servicesOrchestratorValues.urls.cdn);
				clearStorageData();
				clearPartnerData();
				clearHasPickedLocation();
				setLocation(locationData);
				setConfig(locationData.Partner.code, { theming, partnerSettings: partnerConfig });
				setAttribute("category", partnerConfig.supportedDeviceTypes[0] ?? "smartphone");
				logger.injectCommonProp("pandas_location_id", locationData.id);
				logger.injectCommonProp("pandas_partner_code", locationData.Partner.code);
				setHasPickedLocation(true);
				setSelectedLocation(undefined);
				if (setShowPopup) setShowPopup(false);
				const partnerCode = locationData.Partner.code;
				const firstLanguage = partnerConfig.languages[0];
				const locale = (firstLanguage ? languageToi18nCode(firstLanguage) : null) ?? DEFAULT_LOCALE;
				navigate({ to: "/$locale/$partner/home", params: { locale, partner: partnerCode } });
			}
		} catch (error) {
			if (error instanceof NetworkError) {
				setError("Failed to change location.Unstable network try again");
			} else if (error instanceof ApiError) {
				setError("No Valid Configuration for this location");
			}
			logger.error("FLOW", `Failed to change location`, error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const fetchPlatformPartners = async () => {
			try {
				const data = await platformApi.getAllPartners();
				setPlatformPartners(data);
			} catch (error) {
				logger.error("FLOW", "Failed to fetch platform partners", error);
			}
		};
		fetchPlatformPartners();
	}, []);

	const handlePartnerSelection = async (partnerId: string) => {
		try {
			const data = await platformApi.getLocationsByPartner(partnerId, { terminalType: "trade_in_app" });
			setPartnerLocations(data);
		} catch (error) {
			logger.error("FLOW", "Failed to fetch partner locations", error);
		}
	};
	const hasPartnerLocations = partnerLocations.length !== 0;
	return (
		<>
			<PopUp
				className="px-6"
				isOpen={showPopup}
				setIsOpen={
					setShowPopup
						? (value) => {
								setShowPopup(value);
								setSelectedLocation(undefined);
							}
						: undefined
				}
			>
				<div className="inset-0 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg max-w-lg w-full px-6">
						<div className="text-center">
							<h1 className="text-3xl font-semibold">
								{setShowPopup ? t("PopUp.Change location") : "Select Store Location"}
							</h1>
						</div>
						<div className="space-y-6 my-8">
							<Select
								onValueChange={(value) => {
									setSelectedLocation(undefined);
									setSelectedPartner(value);
									handlePartnerSelection(value);
									setError(null);
								}}
								disabled={loading}
							>
								<SelectTrigger>
									<SelectValue placeholder="Partner" />
								</SelectTrigger>
								<SelectContent>
									{platformPartners.map((partner) => (
										<SelectItem
											className="bg-white hover:bg-grey-100 cursor-pointer"
											key={partner.id}
											value={partner.id}
										>
											{partner.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Select
								value={selectedLocation ?? ""}
								key={selectedLocation}
								onValueChange={(value) => {
									setSelectedLocation(value);
									setError(null);
								}}
								disabled={!selectedPartner || loading}
							>
								<SelectTrigger disabled={!hasPartnerLocations}>
									<SelectValue
										placeholder={
											hasPartnerLocations ? t("InputField.Select Location") : t("InputField.No Available Locations")
										}
									/>
								</SelectTrigger>
								<SelectContent>
									{partnerLocations.map((location, index) => (
										<SelectItem
											className="bg-white hover:bg-grey-100 cursor-pointer"
											key={index}
											value={location.id}
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											{location.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center justify-center">
							<Button
								label={t("Buttons.Apply")}
								disabled={!selectedLocation || loading}
								onClick={() => {
									if (selectedLocation) {
										handleLocationSave(selectedLocation);
									}
								}}
								loading={loading}
								size={"medium"}
							></Button>
						</div>
						{error && <p className="text-center pt-6 text-red">{error}</p>}
					</div>
				</div>
			</PopUp>
		</>
	);
}
